<template>
  <div class="w-full relative">

    <!-- ── Selected item card ── -->
    <div
      v-if="internal"
      class="input input-bordered w-full flex gap-2 items-center cursor-default"
    >
      <div class="w-6 text-center opacity-70 shrink-0">
        {{ locationTypeFromCode(internal.kind) }}
      </div>
      <div class="flex-1 min-w-0 flex items-baseline gap-1 truncate">
        <span class="font-semibold truncate">{{ internal.label }}</span>
        <span v-if="internal.value" class="text-xs opacity-60 shrink-0">({{ internal.value }})</span>
      </div>
      <button
        type="button"
        class="btn btn-ghost btn-xs btn-circle shrink-0"
        title="Clear selection"
        @click="clearSelection"
      >
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <!-- ── Search input ── -->
    <input
      v-else
      ref="inputEl"
      v-model="query"
      :placeholder="placeholder"
      class="input input-bordered w-full"
      autocomplete="off"
      @focus="onFocus"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter.prevent="selectActive"
      @keydown.esc.prevent="close"
    />

    <div
      v-if="open && (loading || items.length > 0 || emptyMessage)"
      class="mt-2 rounded-xl border border-base-300 bg-base-100 shadow max-h-80 overflow-auto z-50 absolute w-full"
      role="listbox"
    >
      <div v-if="loading" class="p-3 text-sm opacity-70">Searching…</div>

      <template v-for="it in flatItemsWithMeta" :key="it.__header ? 'h:' + it.title : it.kind + ':' + it.value + ':' + it.nhi">
        <div
          v-if="it.__header"
          class="px-3 py-2 text-xs font-semibold opacity-70 uppercase tracking-wide"
          role="presentation"
        >
          {{ it.title }}
        </div>

        <button
          v-else
          type="button"
          class="w-full text-left px-3 py-2 flex gap-3 items-start hover:bg-base-200"
          :class="it.nhi === activeIndex ? 'bg-base-200' : ''"
          role="option"
          @mousedown.prevent="select(it)"
        >
          <div class="w-6 text-center mt-0.5 opacity-70">
            <span v-if="it.kind === 'airport'">✈️</span>
            <span v-else-if="it.kind === 'city'">📍</span>
            <span v-else-if="it.kind === 'country'">🌍</span>
            <span v-else-if="it.kind === 'region'">🌐</span>
            <span v-else>📍</span>
          </div>
          <div class="flex-1">
            <div class="font-semibold">{{ it.label }}</div>
            <div v-if="it.subLabel" class="text-xs opacity-70">{{ it.subLabel }}</div>
          </div>
          <div class="text-xs opacity-60 uppercase">{{ it.value }}</div>
        </button>
      </template>

      <div v-if="!loading && emptyMessage" class="p-3 text-sm opacity-70">
        {{ emptyMessage }}
      </div>
    </div>

    <div v-if="debug" class="mt-2 text-xs opacity-70">
      <div><b>kind:</b> {{ internal?.kind }}</div>
      <div><b>label:</b> {{ internal?.label }}</div>
      <div><b>value:</b> {{ internal?.value }}</div>
      <div><b>raw type:</b> {{ internal?.raw?.type }}</div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { flightAutocomplete } from "@/models/flightAutocomplete.js";
import {locationTypeFromCode} from "../../common/functions.js";

const props = defineProps({
  modelValue: { type: Object, default: null }, // {kind, label, value, subLabel, raw}
  placeholder: { type: String, default: "Search country, city, airport…" },
  minChars: { type: Number, default: 2 },
  debounceMs: { type: Number, default: 350 },
  debug: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue"]);

const inputEl = ref(null);
const internal = ref(props.modelValue);
const query = ref(props.modelValue?.label ?? "");

const items = ref([]);
const loading = ref(false);
const open = ref(false);
const activeIndex = ref(-1);
const lastRequestId = ref(0);

// Sync internal state when modelValue is changed externally (e.g. edit mode or flight switch)
watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal !== internal.value) {
      internal.value = newVal;
      query.value = newVal?.label ?? "";
    }
  }
);

const emptyMessage = computed(() => {
  const q = query.value.trim();
  if (!open.value) return "";
  if (loading.value) return "";
  if (q.length < props.minChars) return q.length === 0 ? "" : `Type at least ${props.minChars} characters…`;
  if (items.value.length === 0) return "No results";
  return "";
});

// Group items by kind for display, order: cities → airports → countries → regions
const groupedItems = computed(() => {
  const groups = { airport: [], city: [], country: [], region: [] };

  for (const it of items.value || []) {
    const g = groups[it?.kind];
    if (g) g.push(it);
  }

  const out = [];
  const pushGroup = (title, arr) => {
    if (!arr.length) return;
    out.push({ __header: true, title });
    out.push(...arr);
  };

  pushGroup("Regions", groups.region);
  pushGroup("Countries", groups.country);
  pushGroup("Cities", groups.city);
  pushGroup("Airports", groups.airport);
  return out;
});

// Augment each non-header item with a non-header index (nhi) for correct keyboard highlighting
const flatItemsWithMeta = computed(() => {
  let nhi = -1;
  return groupedItems.value.map((it) => ({
    ...it,
    nhi: it.__header ? -1 : ++nhi,
  }));
});

let lastQuery = "";
let debounceTimer = null;

watch(
  () => query.value,
  (newVal) => {
    const q = (newVal ?? "").trim();
    if (q === lastQuery) return;
    lastQuery = q;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => runSearch(), props.debounceMs);
  }
);

function close() {
  open.value = false;
  activeIndex.value = -1;
}

function onFocus() {
  if ((items.value?.length ?? 0) > 0) open.value = true;
}

function move(delta) {
  if (!open.value) return;
  const list = flatItemsWithMeta.value.filter((x) => !x.__header);
  if (list.length === 0) return;
  const max = list.length - 1;
  const next = (activeIndex.value < 0 ? 0 : activeIndex.value) + delta;
  activeIndex.value = Math.max(0, Math.min(max, next));
}

function selectActive() {
  if (!open.value) return;
  const list = flatItemsWithMeta.value.filter((x) => !x.__header);
  const idx = activeIndex.value;
  if (idx >= 0 && idx < list.length) select(list[idx]);
}

function select(sel) {
  internal.value = sel;
  emit("update:modelValue", sel);
  query.value = sel?.label ?? "";
  close();
}

function clearSelection() {
  internal.value = null;
  emit("update:modelValue", null);
  query.value = "";
  items.value = [];
  close();
  nextTick(() => inputEl.value?.focus());
}

/**
 * Normalize a tagged API result into the uniform {kind, label, value, subLabel, raw} shape.
 *
 * Region:  { id, name, type: "region" }
 * Country: { id, name, code, code3, currency, flag, region: {id, name}, type: "country" }
 * City:    { id, name, code, lat, lng, country: {code, name, flag}, type: "city" }
 * Airport: { id, name, iata_code, icao_code, city, lat, lng, is_major, is_international,
 *            country: {code, name, flag}, type: "airport" }
 */
function normalizeFlightLocationSuggestion(s) {
  if (!s || !s.type) return null;
  const safe = (v, fallback = "") => (v == null ? fallback : v);

  switch (s.type) {
    case "region": {
      return {
        kind: "region",
        label: safe(s.name),
        subLabel: "",
        value: safe(s.id),
        raw: {kind: "region", code: s.id, name: safe(s.name)},
      };
    }

    case "country": {
      const name = safe(s.name);
      const code = safe(s.code);
      const regionName = safe(s.region?.name);
      return {
        kind: "country",
        label: name,
        subLabel: regionName,
        value: code || name,
        raw: {kind: "country", code: s.id, name: name},
      };
    }

    case "city": {
      const name = safe(s.name);
      const code = safe(s.code);
      const countryName = safe(s.country?.name);
      return {
        kind: "city",
        label: name,
        subLabel: countryName,
        value: code || name,
        raw: {kind: "city", code: s.id, name: name},
      };
    }

    case "airport": {
      const name = safe(s.name);
      const iata = safe(s.iata_code);
      const icao = safe(s.icao_code);
      const city = safe(s.city);
      const countryName = safe(s.country?.name);
      const locationParts = [city, countryName].filter(Boolean).join(", ");
      const codePart = iata ? `(${iata})` : icao ? `(${icao})` : "";
      return {
        kind: "airport",
        label: name,
        subLabel: city,
        value: iata || icao || name,
        raw: {kind: "airport", code: s.id, name: name || city || iata},
      };
    }

    default:
      return null;
  }
}

async function runSearch() {
  if (internal.value) return; // selection active — suppress dropdown until cleared

  const q = query.value.trim();

  if (internal.value && internal.value.label !== q) {
    internal.value = null;
    emit("update:modelValue", null);
  }

  if (q.length < props.minChars) {
    items.value = [];
    loading.value = false;
    open.value = q.length > 0;
    activeIndex.value = -1;
    return;
  }

  const requestId = ++lastRequestId.value;
  loading.value = true;
  open.value = true;

  try {
    const raw = await flightAutocomplete({ q });

    if (requestId !== lastRequestId.value) return;

    const flat = raw.map(normalizeFlightLocationSuggestion);
    items.value = flat;
    activeIndex.value = flat.length > 0 ? 0 : -1;
  } catch (e) {
    if (requestId !== lastRequestId.value) return;
    items.value = [];
    activeIndex.value = -1;
    console.error(e);
  } finally {
    if (requestId === lastRequestId.value) loading.value = false;
  }
}

function onGlobalClick(ev) {
  const root = inputEl.value?.closest?.(".relative") || null;
  if (!root) return;
  if (root.contains(ev.target)) return;
  close();
}

onMounted(() => window.addEventListener("mousedown", onGlobalClick));
onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer);
  window.removeEventListener("mousedown", onGlobalClick);
});
</script>
